<template>
  <div class="wishlist-page">

    <!-- Page Header -->
    <section class="page-header">
      <div class="container">
        <div class="header-inner">
          <div>
            <span class="eyebrow-pill">{{ isSharedView ? 'Gedeeld met jou' : 'Mijn verlanglijst' }}</span>
            <h1 class="page-title">
              {{ isSharedView
                ? 'Gedeelde verlanglijst'
                : (wishlistItems.length > 0
                  ? `${wishlistItems.length} ${wishlistItems.length === 1 ? 'product' : 'producten'} opgeslagen`
                  : 'Verlanglijst') }}
            </h1>
          </div>

          <div v-if="wishlistItems.length > 0 && !isSharedView" class="header-actions">
            <button @click="confirmClearAll" class="btn-ghost-sm">
              <i class="bi bi-trash"></i>
              Alles wissen
            </button>
            <button @click="shareWishlist" class="btn-ghost-sm">
              <i class="bi bi-share"></i>
              Delen
            </button>
          </div>
        </div>
      </div>
    </section>

    <!-- Gedeelde-lijst banner -->
    <div v-if="isSharedView && !sharedExpired && !loading && wishlistItems.length" class="container">
      <div class="shared-banner">
        <span class="shared-banner__text"><i class="bi bi-share-fill"></i> Je bekijkt een <strong>gedeelde verlanglijst</strong>.</span>
        <button @click="addAllToMyWishlist" class="shared-banner__btn">
          <i class="bi bi-heart"></i> Toevoegen aan mijn verlanglijst
        </button>
      </div>
    </div>

    <!-- Verlopen/onbekende gedeelde lijst -->
    <section v-if="sharedExpired" class="empty-section">
      <div class="container">
        <div class="empty-inner">
          <div class="empty-icon-wrap"><i class="bi bi-clock-history"></i></div>
          <h2 class="empty-title">Deze verlanglijst is niet meer beschikbaar</h2>
          <p class="empty-text">Een gedeelde verlanglijst blijft 3 dagen geldig. Vraag de afzender om een nieuwe link, of stel zelf een verlanglijst samen.</p>
          <router-link to="/producten" class="btn-primary-cta"><i class="bi bi-bag"></i> Ontdek producten</router-link>
        </div>
      </div>
    </section>

    <!-- Loading skeleton (voorkomt layout shift) -->
    <section v-else-if="loading" class="wishlist-section" aria-hidden="true">
      <div class="container">
        <div class="skeleton-toolbar"></div>
        <div class="products-grid">
          <div v-for="n in 6" :key="n" class="product-card skeleton-card">
            <div class="card-image-wrap sk"></div>
            <div class="card-body">
              <div class="sk sk-line" style="width:40%;height:11px;margin-bottom:12px"></div>
              <div class="sk sk-line" style="width:90%;height:14px;margin-bottom:8px"></div>
              <div class="sk sk-line" style="width:65%;height:14px;margin-bottom:16px"></div>
              <div class="sk sk-line" style="width:45%;height:24px;margin-bottom:16px"></div>
              <div class="sk sk-line" style="width:100%;height:44px;border-radius:8px"></div>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Empty State -->
    <section v-else-if="wishlistItems.length === 0" class="empty-section">
      <div class="container">
        <div class="empty-inner">
          <div class="empty-icon-wrap">
            <i class="bi bi-heart"></i>
          </div>
          <h2 class="empty-title">Je verlanglijst is leeg</h2>
          <p class="empty-text">
            Ontdek onze producten en voeg je favorieten toe aan je verlanglijst om ze later gemakkelijk terug te vinden.
          </p>
          <router-link to="/producten" class="btn-primary-cta">
            <i class="bi bi-bag"></i>
            Ontdek producten
          </router-link>

          <div class="quick-links-wrap">
            <p class="quick-links-label">Populaire categorieën</p>
            <div class="quick-links">
              <router-link to="/categorie/thuisbatterijen" class="quick-link">
                <i class="bi bi-battery-charging"></i>
                Thuisbatterijen
              </router-link>
              <router-link to="/categorie/laadpalen" class="quick-link">
                <i class="bi bi-ev-station"></i>
                Laadpalen
              </router-link>
              <router-link to="/categorie/zonnepanelen" class="quick-link">
                <i class="bi bi-sun"></i>
                Zonnepanelen
              </router-link>
              <router-link to="/acties" class="quick-link quick-link--highlight">
                <i class="bi bi-percent"></i>
                Acties
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>

    <!-- Wishlist Items -->
    <section v-else class="wishlist-section">
      <div class="container">

        <!-- Toolbar -->
        <div class="toolbar">
          <div class="toolbar-left">
            <div class="view-toggle">
              <button
                @click="viewMode = 'grid'"
                :class="['view-btn', { active: viewMode === 'grid' }]"
                title="Rasterweergave"
              >
                <i class="bi bi-grid"></i>
              </button>
              <button
                @click="viewMode = 'list'"
                :class="['view-btn', { active: viewMode === 'list' }]"
                title="Lijstweergave"
              >
                <i class="bi bi-list-ul"></i>
              </button>
            </div>

            <div class="sort-wrap">
              <label for="sort-select" class="sort-label">Sorteren:</label>
              <select id="sort-select" v-model="sortBy" class="sort-select">
                <option value="date-desc">Laatst toegevoegd</option>
                <option value="date-asc">Eerst toegevoegd</option>
                <option value="name-asc">Naam (A–Z)</option>
                <option value="name-desc">Naam (Z–A)</option>
                <option value="price-asc">Prijs (laag–hoog)</option>
                <option value="price-desc">Prijs (hoog–laag)</option>
              </select>
            </div>
          </div>

          <button
            @click="addAllToCart"
            class="btn-outline-yellow"
            :disabled="availableCount === 0"
          >
            <i class="bi bi-cart"></i>
            Alles in winkelwagen
          </button>
        </div>

        <!-- Products Grid -->
        <div :class="['products-grid', viewMode === 'list' ? 'products-grid--list' : '']">
          <div
            v-for="item in sortedWishlistItems"
            :key="item.id"
            :class="['product-card', viewMode === 'list' ? 'product-card--list' : '']"
          >
            <!-- Remove button -->
            <button
              @click="removeFromWishlist(item.id)"
              class="card-remove-btn"
              title="Verwijder van verlanglijst"
            >
              <i class="bi bi-x-lg"></i>
            </button>

            <!-- Image -->
            <router-link :to="`/product/${item.slug}`" class="card-image-link">
              <div class="card-image-wrap">
                <img
                  v-if="item.images && item.images.length > 0"
                  :src="sized(item.images[0].src, 400)"
                  :alt="item.name"
                  width="400" height="300"
                  loading="lazy" decoding="async"
                  class="card-image"
                >
                <div v-else class="card-image-placeholder">
                  <i class="bi bi-image"></i>
                </div>

                <!-- Stock badge -->
                <span v-if="item.stock_status === 'outofstock'" class="stock-badge stock-badge--out">
                  Uitverkocht
                </span>
                <span v-else-if="item.stock_quantity && item.stock_quantity < 5" class="stock-badge stock-badge--low">
                  Nog {{ item.stock_quantity }} op voorraad
                </span>
              </div>
            </router-link>

            <!-- Info -->
            <div class="card-body">
              <div v-if="item.categories && item.categories.length > 0" class="card-category">
                {{ item.categories[0].name }}
              </div>

              <router-link :to="`/product/${item.slug}`" class="card-name">
                {{ item.name }}
              </router-link>

              <div v-if="viewMode === 'list' && item.short_description" class="card-description">
                {{ stripHtml(item.short_description).substring(0, 150) }}...
              </div>

              <div class="card-price">
                <template v-if="item.on_sale">
                  <span class="price-old">€{{ formatPrice(item.regular_price) }}</span>
                  <span class="price-current">€{{ formatPrice(item.sale_price) }}</span>
                </template>
                <span v-else class="price-current">€{{ formatPrice(item.price) }}</span>
              </div>

              <div class="card-actions">
                <button
                  v-if="item.stock_status !== 'outofstock'"
                  @click="addToCart(item)"
                  class="btn-add-to-cart"
                  :disabled="addingToCart[item.id]"
                >
                  <i :class="['bi', addingToCart[item.id] ? 'bi-arrow-repeat spin' : 'bi-cart']"></i>
                  {{ addingToCart[item.id] ? 'Toevoegen...' : 'In winkelwagen' }}
                </button>
                <button v-else class="btn-unavailable" disabled>
                  <i class="bi bi-slash-circle"></i>
                  Niet beschikbaar
                </button>

                <router-link :to="`/product/${item.slug}`" class="btn-view">
                  <i class="bi bi-eye"></i>
                </router-link>
              </div>
            </div>
          </div>
        </div>

        <!-- Summary bar -->
        <div class="summary-bar">
          <div class="summary-stats">
            <div class="stat">
              <span class="stat-value">{{ wishlistItems.length }}</span>
              <span class="stat-label">{{ wishlistItems.length === 1 ? 'product' : 'producten' }}</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat">
              <span class="stat-value">€{{ totalValue }}</span>
              <span class="stat-label">totale waarde</span>
            </div>
            <div class="stat-divider"></div>
            <div class="stat">
              <span class="stat-value">{{ availableCount }}</span>
              <span class="stat-label">beschikbaar</span>
            </div>
          </div>
          <button @click="shareWishlist" class="btn-ghost-sm">
            <i class="bi bi-share"></i>
            Verlanglijst delen
          </button>
        </div>

      </div>
    </section>

    <!-- Recommendations -->
    <section v-if="recommendations.length > 0" class="recommendations-section">
      <div class="container">
        <h2 class="section-title">
          <i class="bi bi-star-fill"></i>
          Ook interessant voor jou
        </h2>
        <div class="recommendations-grid">
          <router-link
            v-for="product in recommendations"
            :key="product.id"
            :to="`/product/${product.slug}`"
            class="rec-card"
          >
            <div class="rec-image-wrap">
              <img
                v-if="product.images && product.images.length > 0"
                :src="sized(product.images[0].src, 240)"
                :alt="product.name"
                width="240" height="240"
                loading="lazy" decoding="async"
              >
            </div>
            <div class="rec-body">
              <p class="rec-name">{{ product.name }}</p>
              <p class="rec-price">€{{ formatPrice(product.price) }}</p>
            </div>
            <button @click.prevent="toggleWishlist(product)" class="rec-wishlist-btn" title="Voeg toe aan verlanglijst">
              <i class="bi bi-heart"></i>
            </button>
          </router-link>
        </div>
      </div>
    </section>

    <!-- Clear-all modal -->
    <transition name="modal">
      <div v-if="showClearModal" class="modal-overlay" @click="showClearModal = false">
        <div ref="clearModalEl" class="modal-box" @click.stop role="dialog" aria-modal="true" aria-label="Verlanglijst wissen">
          <div class="modal-icon">
            <i class="bi bi-exclamation-triangle"></i>
          </div>
          <h3 class="modal-title">Verlanglijst wissen?</h3>
          <p class="modal-text">
            Weet je zeker dat je alle {{ wishlistItems.length }} producten wilt verwijderen?
            Dit kan niet ongedaan worden gemaakt.
          </p>
          <div class="modal-actions">
            <button @click="showClearModal = false" class="modal-btn modal-btn--cancel">
              Annuleren
            </button>
            <button @click="clearAll" class="modal-btn modal-btn--danger">
              <i class="bi bi-trash"></i>
              Ja, alles wissen
            </button>
          </div>
        </div>
      </div>
    </transition>

    <!-- Toast -->
    <transition name="toast">
      <div v-if="toast.show" :class="['toast', toast.type === 'error' ? 'toast--error' : '']">
        <i :class="['bi', toast.icon]"></i>
        <span>{{ toast.message }}</span>
      </div>
    </transition>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { formatPrice } from '@/utils/price'
import { useRouter, useRoute } from 'vue-router'
import { useCartStore } from '@stores/cart'
import { useWishlistStore } from '@stores/wishlist'
import { useEscToClose } from '@composables/useEscToClose'
import { useFocusTrap } from '@composables/useFocusTrap'

const router = useRouter()
const cartStore = useCartStore()
const wishlistStore = useWishlistStore()

// Stuur WP-afbeeldingen via de resize-proxy (verkleind + WebP). Absolute of
// relatieve /wp-content-URL's worden een lichte variant; externe URL's ongemoeid.
const sized = (url, w) => {
  if (!url) return url
  const i = url.indexOf('/wp-content/')
  return i === -1 ? url : `${url.slice(i)}?w=${w}`
}

const route = useRoute()

// State
const loading = ref(true)
const isSharedView = ref(false)
const sharedExpired = ref(false)
const wishlistItems = ref([])
const recommendations = ref([])
const viewMode = ref('grid') // 'grid' | 'list'
const sortBy = ref('date-desc')
const addingToCart = ref({})
const showClearModal = ref(false)
const clearModalEl = ref(null)
useEscToClose(showClearModal, () => { showClearModal.value = false })
useFocusTrap(showClearModal, clearModalEl)
const toast = ref({
  show: false,
  message: '',
  type: 'success',
  icon: 'bi-check-circle'
})

// Computed
const sortedWishlistItems = computed(() => {
  const items = [...wishlistItems.value]

  switch (sortBy.value) {
    case 'date-asc':
      return items.reverse()
    case 'name-asc':
      return items.sort((a, b) => a.name.localeCompare(b.name))
    case 'name-desc':
      return items.sort((a, b) => b.name.localeCompare(a.name))
    case 'price-asc':
      return items.sort((a, b) => parseFloat(a.price) - parseFloat(b.price))
    case 'price-desc':
      return items.sort((a, b) => parseFloat(b.price) - parseFloat(a.price))
    default: // 'date-desc'
      return items
  }
})

const totalValue = computed(() => {
  const total = wishlistItems.value.reduce((sum, item) => {
    const price = item.sale_price || item.price
    return sum + parseFloat(price || 0)
  }, 0)
  return formatPrice(total)
})

const availableCount = computed(() => {
  return wishlistItems.value.filter(item => item.stock_status !== 'outofstock').length
})

// Methods
const stripHtml = (html) => {
  if (!html) return ''
  return html.replace(/<[^>]*>/g, '')
}

const removeFromWishlist = async (productId) => {
  try {
    wishlistStore.removeFromWishlist(productId)
    wishlistItems.value = wishlistItems.value.filter(item => item.id !== productId)
    showToast('Product verwijderd van verlanglijst', 'success', 'bi-check-circle')
  } catch (error) {
    console.error('Error removing from wishlist:', error)
    showToast('Er ging iets mis', 'error', 'bi-exclamation-triangle')
  }
}

const addToCart = async (product) => {
  try {
    addingToCart.value[product.id] = true
    await cartStore.addToCart(product, 1)
    showToast(`${product.name} toegevoegd aan winkelwagen`, 'success', 'bi-check-circle')
  } catch (error) {
    console.error('Error adding to cart:', error)
    showToast('Er ging iets mis', 'error', 'bi-exclamation-triangle')
  } finally {
    addingToCart.value[product.id] = false
  }
}

const addAllToCart = async () => {
  try {
    const availableItems = wishlistItems.value.filter(item => item.stock_status !== 'outofstock')
    for (const item of availableItems) {
      await cartStore.addToCart(item, 1)
    }
    showToast(`${availableItems.length} producten toegevoegd aan winkelwagen`, 'success', 'bi-check-circle')
  } catch (error) {
    console.error('Error adding all to cart:', error)
    showToast('Er ging iets mis', 'error', 'bi-exclamation-triangle')
  }
}

const confirmClearAll = () => {
  showClearModal.value = true
}

const clearAll = () => {
  wishlistStore.clearWishlist()
  wishlistItems.value = []
  showClearModal.value = false
  showToast('Verlanglijst gewist', 'success', 'bi-check-circle')
}

const shareWishlist = async () => {
  const ids = wishlistStore.wishlistIds
  if (!ids.length) return

  // Server-side deellink met unieke hash (3 dagen geldig). Zo ziet de ontvanger
  // JOUW lijst, niet z'n eigen localStorage-lijst.
  let shareUrl = window.location.origin + '/wishlist'
  try {
    const res = await fetch('/api/wishlist/share', {
      method:  'POST',
      headers: { 'Content-Type': 'application/json' },
      body:    JSON.stringify({ ids }),
    })
    if (res.ok) {
      const data = await res.json()
      shareUrl = `${window.location.origin}${data.url}`
    }
  } catch (e) { console.error('share create', e) }

  if (navigator.share) {
    try {
      await navigator.share({
        title: 'Mijn Verlanglijst - Plug2charge',
        text: `Bekijk mijn verlanglijst met ${ids.length} producten`,
        url: shareUrl,
      })
      showToast('Verlanglijst gedeeld', 'success', 'bi-check-circle')
    } catch (error) {
      if (error.name !== 'AbortError') console.error('Error sharing:', error)
    }
  } else {
    try {
      await navigator.clipboard.writeText(shareUrl)
      showToast('Deellink gekopieerd (3 dagen geldig)', 'success', 'bi-check-circle')
    } catch (error) {
      console.error('Error copying to clipboard:', error)
      showToast('Kon link niet kopiëren', 'error', 'bi-exclamation-triangle')
    }
  }
}

// Alle producten uit een gedeelde lijst toevoegen aan je eigen verlanglijst
const addAllToMyWishlist = () => {
  wishlistItems.value.forEach(p => wishlistStore.addToWishlist(p))
  showToast('Toegevoegd aan jouw verlanglijst', 'success', 'bi-heart')
}

const toggleWishlist = (product) => {
  wishlistStore.toggleWishlist(product)
  showToast('Toegevoegd aan verlanglijst', 'success', 'bi-heart')
}

const showToast = (message, type = 'success', icon = 'bi-check-circle') => {
  toast.value = { show: true, message, type, icon }
  setTimeout(() => { toast.value.show = false }, 3000)
}

const fetchWishlistProducts = async () => {
  try {
    wishlistStore.initWishlist()
    const wishlistIds = wishlistStore.wishlistIds

    if (wishlistIds.length === 0) {
      wishlistItems.value = []
      return
    }

    const response = await fetch(
      `/_wc/v3/products?include=${wishlistIds.join(',')}&per_page=${wishlistIds.length}` +
      `&_fields=id,name,slug,price,regular_price,sale_price,on_sale,images,stock_status,stock_quantity,categories,short_description`
    )

    if (response.ok) {
      const products = await response.json()
      products.forEach(p => { wishlistStore._products[p.id] = p })
      wishlistItems.value = products
    }
  } catch (error) {
    console.error('Error fetching wishlist products:', error)
  }
}

const fetchRecommendations = async () => {
  try {
    const response = await fetch('/_wc/v3/products?per_page=4&orderby=popularity')
    if (response.ok) {
      const products = await response.json()
      recommendations.value = products.filter(
        p => !wishlistItems.value.find(w => w.id === p.id)
      )
    }
  } catch (error) {
    console.error('Error fetching recommendations:', error)
  }
}

// Een gedeelde lijst (via ?share=hash) ophalen en tonen
const loadSharedWishlist = async (hash) => {
  isSharedView.value = true
  document.title = 'Gedeelde verlanglijst | Plug2charge'
  try {
    const res = await fetch(`/api/wishlist/shared/${encodeURIComponent(hash)}`)
    if (res.status === 404) { sharedExpired.value = true; return }
    if (!res.ok) return
    const { ids } = await res.json()
    if (ids?.length) {
      const r = await fetch(
        `/_wc/v3/products?include=${ids.join(',')}&per_page=${ids.length}` +
        `&_fields=id,name,slug,price,regular_price,sale_price,on_sale,images,stock_status,stock_quantity,categories`
      )
      if (r.ok) wishlistItems.value = await r.json()
    }
  } catch (e) {
    console.error('shared wishlist', e)
  }
}

// Lifecycle
onMounted(async () => {
  const shareHash = route.query.share
  try {
    if (shareHash) {
      await loadSharedWishlist(String(shareHash))
    } else {
      document.title = 'Mijn Verlanglijst | Plug2charge'
      await fetchWishlistProducts()
    }
  } finally {
    loading.value = false
  }
  if (!isSharedView.value && wishlistItems.value.length > 0) {
    fetchRecommendations()
  }
})
</script>

<style scoped>
/* ============================================
   WISHLIST PAGE - PLUG2CHARGE
   ============================================ */

/* ---- Loading skeleton ---- */
.sk {
  background: linear-gradient(100deg, #eef0f2 30%, #f6f7f8 50%, #eef0f2 70%);
  background-size: 200% 100%;
  animation: sk-shimmer 1.3s ease-in-out infinite;
  border-radius: 6px;
}
.sk-line { display: block; }
.skeleton-card { pointer-events: none; }
.skeleton-card .card-image-wrap { aspect-ratio: 4 / 3; }
.skeleton-toolbar {
  height: 44px;
  border-radius: 8px;
  margin-bottom: 2rem;
  background: linear-gradient(100deg, #eef0f2 30%, #f6f7f8 50%, #eef0f2 70%);
  background-size: 200% 100%;
  animation: sk-shimmer 1.3s ease-in-out infinite;
}
@keyframes sk-shimmer {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}
@media (prefers-reduced-motion: reduce) {
  .sk, .skeleton-toolbar { animation: none; }
}

/* ---- Gedeelde-lijst banner ---- */
.shared-banner {
  display: flex; align-items: center; justify-content: space-between; gap: 1rem; flex-wrap: wrap;
  background: #fffbe6; border: 1.5px solid #FFD500; border-radius: 12px;
  padding: 1rem 1.25rem; margin: 0 0 1.5rem;
}
.shared-banner__text { font-size: .95rem; color: #111; }
.shared-banner__text i { color: #b58a00; margin-right: .35rem; }
.shared-banner__btn {
  display: inline-flex; align-items: center; gap: .5rem; padding: .6rem 1.1rem;
  background: #111; color: #fff; border: none; border-radius: 8px; font-weight: 700;
  font-size: .875rem; cursor: pointer; transition: background .2s; white-space: nowrap;
}
.shared-banner__btn:hover { background: #333; }

.wishlist-page {
  background: #fff;
  min-height: 100vh;
}

.container {
  max-width: 1280px;
  margin: 0 auto;
  padding: 0 1.5rem;
  width: 100%;
}

/* ---- Page Header ---- */
.page-header {
  background: #fff;
  border-bottom: 1px solid #f0f0f0;
  padding: 2.5rem 0 2rem;
}

.header-inner {
  display: flex;
  justify-content: space-between;
  align-items: flex-end;
  gap: 1.5rem;
}

.eyebrow-pill {
  display: inline-block;
  background: #FFD500;
  color: #111;
  font-size: 0.75rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  padding: 0.3rem 0.875rem;
  border-radius: 50px;
  margin-bottom: 0.75rem;
}

.page-title {
  font-size: 2rem;
  font-weight: 900;
  color: #111;
  margin: 0;
  line-height: 1.2;
}

.header-actions {
  display: flex;
  gap: 0.75rem;
  flex-shrink: 0;
}

/* ---- Ghost small button ---- */
.btn-ghost-sm {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 1rem;
  background: transparent;
  border: 1.5px solid #e9eaec;
  border-radius: 8px;
  color: #555;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: pointer;
  transition: border-color 0.2s, color 0.2s, background 0.2s;
  white-space: nowrap;
}

.btn-ghost-sm:hover {
  border-color: #FFD500;
  color: #111;
  background: #fffbe6;
}

/* ---- Empty state ---- */
.empty-section {
  padding: 5rem 0 6rem;
}

.empty-inner {
  text-align: center;
  max-width: 540px;
  margin: 0 auto;
}

.empty-icon-wrap {
  width: 100px;
  height: 100px;
  background: #fffbe6;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 3rem;
  color: #FFD500;
  margin: 0 auto 1.75rem;
}

.empty-title {
  font-size: 1.75rem;
  font-weight: 800;
  color: #111;
  margin-bottom: 0.875rem;
}

.empty-text {
  font-size: 1rem;
  color: #666;
  line-height: 1.7;
  margin-bottom: 2rem;
}

.btn-primary-cta {
  display: inline-flex;
  align-items: center;
  gap: 0.625rem;
  padding: 0.875rem 2rem;
  background: #FFD500;
  color: #111;
  font-weight: 700;
  font-size: 0.9375rem;
  border-radius: 8px;
  text-decoration: none;
  transition: background 0.2s, box-shadow 0.2s;
}

.btn-primary-cta:hover {
  background: #ffb800;
  box-shadow: 0 4px 16px rgba(255, 213, 0, 0.4);
}

.quick-links-wrap {
  margin-top: 2.5rem;
}

.quick-links-label {
  font-size: 0.8125rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  color: #999;
  margin-bottom: 0.875rem;
}

.quick-links {
  display: flex;
  justify-content: center;
  gap: 0.75rem;
  flex-wrap: wrap;
}

.quick-link {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  padding: 0.5rem 1.125rem;
  background: #fff;
  border: 1.5px solid #e9eaec;
  border-radius: 50px;
  text-decoration: none;
  color: #444;
  font-size: 0.875rem;
  font-weight: 600;
  transition: border-color 0.2s, background 0.2s;
}

.quick-link:hover {
  border-color: #FFD500;
  background: #fffbe6;
}

.quick-link--highlight {
  background: #FFD500;
  border-color: #FFD500;
  color: #111;
}

.quick-link--highlight:hover {
  background: #ffb800;
  border-color: #ffb800;
}

/* ---- Wishlist section ---- */
.wishlist-section {
  padding: 0 0 4rem;
}

/* ---- Toolbar ---- */
.toolbar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.5rem;
  padding: 1.25rem 0;
  border-bottom: 1.5px solid #e9eaec;
  margin-bottom: 2rem;
}

.toolbar-left {
  display: flex;
  align-items: center;
  gap: 1.25rem;
}

.view-toggle {
  display: flex;
  gap: 0.375rem;
}

.view-btn {
  width: 36px;
  height: 36px;
  background: transparent;
  border: 1.5px solid #e9eaec;
  border-radius: 6px;
  color: #888;
  font-size: 0.9375rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 150ms cubic-bezier(0.23,1,0.32,1), background 150ms cubic-bezier(0.23,1,0.32,1), transform 150ms cubic-bezier(0.23,1,0.32,1);
}

.view-btn:hover {
  border-color: #FFD500;
  color: #111;
}

.view-btn.active {
  background: #FFD500;
  border-color: #FFD500;
  color: #111;
}

.sort-wrap {
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.sort-label {
  font-size: 0.875rem;
  font-weight: 600;
  color: #888;
  white-space: nowrap;
}

.sort-select {
  padding: 0.5rem 0.875rem;
  border: 1.5px solid #e9eaec;
  border-radius: 6px;
  background: #fff;
  font-size: 0.875rem;
  color: #111;
  cursor: pointer;
  transition: border-color 0.2s;
}

.sort-select:focus {
  outline: none;
  border-color: #FFD500;
}

/* ---- Outlined yellow button ---- */
.btn-outline-yellow {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.25rem;
  background: transparent;
  border: 2px solid #FFD500;
  border-radius: 8px;
  color: #111;
  font-size: 0.875rem;
  font-weight: 700;
  cursor: pointer;
  transition: background 0.2s, box-shadow 0.2s;
  white-space: nowrap;
}

.btn-outline-yellow:hover:not(:disabled) {
  background: #FFD500;
  box-shadow: 0 2px 12px rgba(255, 213, 0, 0.35);
}

.btn-outline-yellow:disabled {
  opacity: 0.45;
  cursor: not-allowed;
}

/* ---- Products grid ---- */
.products-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 1.5rem;
  margin-bottom: 2.5rem;
}

.products-grid--list {
  grid-template-columns: 1fr;
}

/* ---- Product card ---- */
.product-card {
  position: relative;
  background: #fff;
  border: 1.5px solid #e9eaec;
  border-radius: 12px;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: border-color 0.25s, box-shadow 0.25s;
}

.product-card:hover {
  border-color: #FFD500;
  box-shadow: 0 4px 24px rgba(255, 213, 0, 0.18);
}

.product-card--list {
  flex-direction: row;
}

/* Remove button */
.card-remove-btn {
  position: absolute;
  top: 0.625rem;
  right: 0.625rem;
  z-index: 2;
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid #e9eaec;
  border-radius: 50%;
  color: #888;
  font-size: 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 150ms cubic-bezier(0.23,1,0.32,1), background 150ms cubic-bezier(0.23,1,0.32,1), transform 150ms cubic-bezier(0.23,1,0.32,1);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.card-remove-btn:hover {
  background: #f5f5f5;
  color: #111;
  border-color: #bbb;
}

/* Image */
.card-image-link {
  display: block;
  text-decoration: none;
  flex-shrink: 0;
}

.product-card--list .card-image-link {
  width: 220px;
}

.card-image-wrap {
  position: relative;
  aspect-ratio: 4 / 3;
  background: #fff;
  overflow: hidden;
}

.product-card--list .card-image-wrap {
  height: 100%;
  aspect-ratio: unset;
}

.card-image {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.3s ease;
}

.product-card:hover .card-image {
  transform: scale(1.04);
}

.card-image-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2.5rem;
  color: #ccc;
}

/* Stock badges */
.stock-badge {
  position: absolute;
  bottom: 0.625rem;
  left: 0.625rem;
  padding: 0.25rem 0.625rem;
  border-radius: 50px;
  font-size: 0.6875rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.stock-badge--out {
  background: #dc3545;
  color: #fff;
}

.stock-badge--low {
  background: #FFD500;
  color: #111;
}

/* Card body */
.card-body {
  padding: 1.25rem;
  display: flex;
  flex-direction: column;
  flex: 1;
}

.card-category {
  font-size: 0.75rem;
  font-weight: 600;
  color: #999;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  margin-bottom: 0.375rem;
}

.card-name {
  font-size: 0.9375rem;
  font-weight: 700;
  color: #111;
  text-decoration: none;
  line-height: 1.4;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  margin-bottom: 0.625rem;
  transition: color 0.15s;
}

.card-name:hover {
  color: #555;
}

.card-description {
  font-size: 0.875rem;
  color: #666;
  line-height: 1.6;
  margin-bottom: 0.75rem;
}

.card-price {
  display: flex;
  align-items: baseline;
  gap: 0.625rem;
  margin-bottom: 1rem;
  margin-top: auto;
}

.price-current {
  font-size: 1.375rem;
  font-weight: 800;
  color: #111;
}

.price-old {
  font-size: 1rem;
  color: #bbb;
  text-decoration: line-through;
  font-weight: 400;
}

/* Card actions */
.card-actions {
  display: flex;
  gap: 0.625rem;
}

.btn-add-to-cart {
  flex: 1;
  padding: 0.75rem 1rem;
  background: #FFD500;
  color: #111;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 700;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  transition: background 0.2s, box-shadow 0.2s;
}

.btn-add-to-cart:hover:not(:disabled) {
  background: #ffb800;
  box-shadow: 0 3px 12px rgba(255, 213, 0, 0.4);
}

.btn-add-to-cart:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-unavailable {
  flex: 1;
  padding: 0.75rem 1rem;
  background: #f5f5f5;
  color: #bbb;
  border: none;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  cursor: not-allowed;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
}

.btn-view {
  width: 42px;
  height: 42px;
  background: #fff;
  border: 1.5px solid #e9eaec;
  border-radius: 8px;
  color: #555;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  text-decoration: none;
  flex-shrink: 0;
  transition: border-color 0.2s, color 0.2s, background 0.2s;
}

.btn-view:hover {
  border-color: #FFD500;
  background: #fffbe6;
  color: #111;
}

/* ---- Summary bar ---- */
.summary-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  gap: 1.5rem;
  padding: 1.5rem 2rem;
  background: #fafafa;
  border: 1.5px solid #e9eaec;
  border-radius: 12px;
}

.summary-stats {
  display: flex;
  align-items: center;
  gap: 1.5rem;
}

.stat {
  display: flex;
  flex-direction: column;
  align-items: center;
}

.stat-value {
  font-size: 1.25rem;
  font-weight: 900;
  color: #111;
  line-height: 1.1;
}

.stat-label {
  font-size: 0.75rem;
  color: #888;
  font-weight: 500;
}

.stat-divider {
  width: 1px;
  height: 2rem;
  background: #e9eaec;
}

/* ---- Recommendations ---- */
.recommendations-section {
  padding: 3rem 0 4rem;
  border-top: 1.5px solid #e9eaec;
}

.section-title {
  font-size: 1.375rem;
  font-weight: 800;
  color: #111;
  margin-bottom: 1.75rem;
  display: flex;
  align-items: center;
  gap: 0.625rem;
}

.section-title i {
  color: #FFD500;
  font-size: 1rem;
}

.recommendations-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1.25rem;
}

.rec-card {
  position: relative;
  background: #fff;
  border: 1.5px solid #e9eaec;
  border-radius: 12px;
  overflow: hidden;
  text-decoration: none;
  transition: border-color 0.25s, box-shadow 0.25s, transform 0.25s;
}

.rec-card:hover {
  border-color: #FFD500;
  box-shadow: 0 6px 20px rgba(255, 213, 0, 0.15);
  transform: translateY(-4px);
}

.rec-image-wrap {
  aspect-ratio: 1;
  background: #f8f8f8;
  overflow: hidden;
}

.rec-image-wrap img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
  transition: transform 0.3s;
}

.rec-card:hover .rec-image-wrap img {
  transform: scale(1.04);
}

.rec-body {
  padding: 0.875rem 1rem;
}

.rec-name {
  font-size: 0.875rem;
  font-weight: 700;
  color: #111;
  margin-bottom: 0.25rem;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  line-height: 1.4;
}

.rec-price {
  font-size: 1rem;
  font-weight: 800;
  color: #111;
  margin: 0;
}

.rec-wishlist-btn {
  position: absolute;
  top: 0.625rem;
  right: 0.625rem;
  width: 32px;
  height: 32px;
  background: rgba(255, 255, 255, 0.92);
  border: 1px solid #e9eaec;
  border-radius: 50%;
  color: #888;
  font-size: 0.75rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: border-color 150ms cubic-bezier(0.23,1,0.32,1), background 150ms cubic-bezier(0.23,1,0.32,1), transform 150ms cubic-bezier(0.23,1,0.32,1);
  box-shadow: 0 1px 4px rgba(0, 0, 0, 0.08);
}

.rec-wishlist-btn:hover {
  color: #e53935;
  border-color: #e53935;
  background: #fff;
}

/* ---- Modal ---- */
.modal-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 2rem;
}

.modal-box {
  background: #fff;
  border-radius: 16px;
  padding: 2.5rem;
  max-width: 480px;
  width: 100%;
  text-align: center;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
}

.modal-icon {
  width: 68px;
  height: 68px;
  background: #fff8e1;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: #f59e0b;
  margin: 0 auto 1.25rem;
}

.modal-title {
  font-size: 1.5rem;
  font-weight: 800;
  color: #111;
  margin-bottom: 0.875rem;
}

.modal-text {
  font-size: 0.9375rem;
  color: #666;
  line-height: 1.6;
  margin-bottom: 2rem;
}

.modal-actions {
  display: flex;
  gap: 0.875rem;
  justify-content: center;
}

.modal-btn {
  padding: 0.8rem 1.75rem;
  border: none;
  border-radius: 8px;
  font-weight: 700;
  font-size: 0.9375rem;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  transition: border-color 150ms cubic-bezier(0.23,1,0.32,1), background 150ms cubic-bezier(0.23,1,0.32,1), transform 150ms cubic-bezier(0.23,1,0.32,1);
}

.modal-btn--cancel {
  background: #f5f5f5;
  color: #555;
}

.modal-btn--cancel:hover {
  background: #e9eaec;
}

.modal-btn--danger {
  background: #dc3545;
  color: #fff;
}

.modal-btn--danger:hover {
  background: #c82333;
}

/* ---- Toast ---- */
.toast {
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  background: #111;
  color: #fff;
  padding: 0.875rem 1.375rem;
  border-radius: 8px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  gap: 0.625rem;
  z-index: 3000;
  font-weight: 600;
  font-size: 0.9375rem;
  max-width: 360px;
}

.toast--error {
  background: #dc3545;
}

/* ---- Spin animation ---- */
@keyframes spin {
  to { transform: rotate(360deg); }
}

.spin {
  animation: spin 0.8s linear infinite;
  display: inline-block;
}

/* ---- Modal transition ---- */
.modal-enter-active,
.modal-leave-active {
  transition: opacity 0.25s ease;
}

.modal-enter-from,
.modal-leave-to {
  opacity: 0;
}

.modal-enter-active .modal-box,
.modal-leave-active .modal-box {
  transition: transform 0.25s ease;
}

.modal-enter-from .modal-box,
.modal-leave-to .modal-box {
  transform: scale(0.92);
}

/* ---- Toast transition ---- */
.toast-enter-active,
.toast-leave-active {
  transition: border-color 200ms cubic-bezier(0.23,1,0.32,1), background 200ms cubic-bezier(0.23,1,0.32,1);
}

.toast-enter-from,
.toast-leave-to {
  opacity: 0;
  transform: translateX(100%);
}

/* ---- Responsive ---- */
@media (max-width: 1199px) {
  .recommendations-grid {
    grid-template-columns: repeat(3, 1fr);
  }
}

@media (max-width: 991px) {
  .products-grid {
    grid-template-columns: repeat(2, 1fr);
  }

  .header-inner {
    flex-direction: column;
    align-items: flex-start;
  }

  .toolbar {
    flex-direction: column;
    align-items: flex-start;
    gap: 1rem;
  }

  .btn-outline-yellow {
    width: 100%;
    justify-content: center;
  }

  .summary-bar {
    flex-direction: column;
    align-items: flex-start;
    gap: 1.25rem;
  }

  .recommendations-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 767px) {
  .page-title {
    font-size: 1.5rem;
  }

  .products-grid {
    grid-template-columns: 1fr;
  }

  .products-grid--list .product-card--list {
    flex-direction: column;
  }

  .products-grid--list .product-card--list .card-image-link {
    width: 100%;
  }

  .summary-stats {
    flex-wrap: wrap;
    gap: 1rem;
  }
}

@media (max-width: 480px) {
  .modal-overlay {
    padding: 1rem;
  }

  .modal-box {
    padding: 1.75rem 1.25rem;
  }

  .modal-actions {
    flex-direction: column;
  }

  .modal-btn {
    width: 100%;
    justify-content: center;
  }

  .toast {
    left: 1rem;
    right: 1rem;
    bottom: 1rem;
    max-width: unset;
  }

  .recommendations-grid {
    grid-template-columns: 1fr;
  }
}
</style>
